import { useState, useEffect } from "react";

import { Card, CardContent, Grid2, Box } from "@mui/material";

import { CardGrid } from "components/CardGrid";
import { EditToggle } from "components/EditToggle";
import { FrmSelectScalar } from "components/formFields/FrmSelectScalar";
import { FrmTextField } from "components/formFields/FrmTextField";
import { CardHeader } from "components/formFields/formCards/CardHeader";
import { FrmCheckbox } from "components/formFields/frm/FrmCheckbox";
import { FrmLocation } from "components/formFields/frm/FrmLocation";

import { i18n } from "services/i18nService";
import { formatCompanyInfo } from "services/utility/formatting";
import { isAddressEmpty } from "services/utility/misc";
import { checkForUnexpectedProps } from "services/utility/misc";

import { STATE_OPTIONS } from "appConstants";

export function ShippingAddress(props) {
  const {
    addressData,
    record,
    onValueBlur,
    title,
    setToOtherAddress,
    addressType,
    residentialAddressType,
    isInError,
    setRecord,
    location,
    ...unexpected
  } = props;
  checkForUnexpectedProps("ShippingAddress", unexpected);

  const isReturnShipment = Boolean(record.returnForShipmentId);
  const [expanded, setExpanded] = useState(!isReturnShipment);

  const { address } = addressData;

  function handleBlur(name, value) {
    return onValueBlur(addressType, { ...addressData, [name]: value });
  }

  useEffect(() => {
    const addressError = isInError(addressType);
    if (addressError) {
      setExpanded(true);
    }
  }, [addressType, isInError]);

  function handleAddressBlur(name, value) {
    return onValueBlur(addressType, {
      ...addressData,
      address: { ...address, [name]: value },
    });
  }

  function handleLocationChange(_, value) {
    if (value) {
      // delete country since it is not tracked
      delete value.address.country;

      const { id, name } = value;
      setRecord((prevRecord) => ({
        ...prevRecord,
        location: { id, name },
        [addressType]: value,
      }));
    }
  }

  return (
    <div style={{ maxWidth: "30rem" }}>
      <Card variant="outlined">
        <CardContent>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <CardHeader text={title} />
            {addressType === "fromAddress" && (
              <FrmLocation
                sendFullObject
                value={location}
                onValueChange={handleLocationChange}
                width="15rem"
              />
            )}
          </Box>
          <Box
            sx={{
              padding: "1rem 0",
              display: "grid",
              gridTemplateColumns: "8fr minmax(auto, 1fr)",
              columnGap: 1,
            }}
          >
            {expanded ? (
              <CardGrid rowGap={0}>
                <FrmTextField
                  name="company"
                  label={i18n("frmLabel.CompanyOrName")}
                  onValueBlur={handleBlur}
                  value={addressData.company}
                  error={isInError(`${addressType}Company`)}
                />
                <FrmTextField
                  name="contact"
                  label={i18n("frmLabel.Contact")}
                  onValueBlur={handleBlur}
                  value={addressData.contact}
                />
                {[1, 2, 3].map((i) => (
                  <FrmTextField
                    key={"line" + i}
                    name={"line" + i}
                    label={`Line ${i}`}
                    maxLength={400}
                    onValueBlur={handleAddressBlur}
                    value={address ? address["line" + i] : ""}
                    error={isInError(`${addressType}Line${i}`)}
                  />
                ))}
                <FrmTextField
                  name="city"
                  label="City"
                  maxLength={255}
                  onValueBlur={handleAddressBlur}
                  value={address.city}
                  error={isInError(`${addressType}City`)}
                />
                <Grid2 container direction="row" spacing={1}>
                  <Grid2 size={6}>
                    <FrmSelectScalar
                      name="stateProvince"
                      label={i18n("frmLabel.State")}
                      options={STATE_OPTIONS}
                      onValueChange={handleAddressBlur}
                      error={isInError(`${addressType}StateProvince`)}
                      value={address.stateProvince}
                    />
                  </Grid2>
                  <Grid2 size={6}>
                    <FrmTextField
                      name="postalCode"
                      label={i18n("frmLabel.PostalCode")}
                      onValueBlur={handleAddressBlur}
                      value={address.postalCode}
                      error={isInError(`${addressType}PostalCode`)}
                      maxLength={30}
                    />
                  </Grid2>
                </Grid2>
                <FrmTextField
                  name="email"
                  label={i18n("frmLabel.Email")}
                  onValueBlur={handleBlur}
                  value={addressData.email}
                />
                <FrmTextField
                  name="phone"
                  label={i18n("frmLabel.Phone")}
                  onValueBlur={handleBlur}
                  value={addressData?.phone}
                />
                <FrmCheckbox
                  name={residentialAddressType}
                  value={record[residentialAddressType]}
                  onValueChange={onValueBlur}
                  label={i18n("frmLabel.ResidentialAddress")}
                />
              </CardGrid>
            ) : (
              <div
                style={{
                  paddingLeft: "1rem",
                  whiteSpace: "pre-line",
                  lineHeight: "1.25rem",
                  overflow: "auto",
                  overflowWrap: "break-word",
                }}
              >
                {isAddressEmpty(addressData) ? (
                  i18n("global.NoAddressEntered")
                ) : (
                  <div>{formatCompanyInfo(addressData, false, true)}</div>
                )}
              </div>
            )}

            <div style={{ textAlign: "right" }}>
              <EditToggle
                toggle={() => setExpanded(!expanded)}
                isExpanded={expanded}
              />
            </div>
          </Box>
        </CardContent>
      </Card>
    </div>
  );
}
